import React, { useState } from 'react';
import { BsTrophy } from 'react-icons/bs';
import { LeaguePlayer } from '../../../services/leagueService';

interface LeagueRankingProps {
  players: LeaguePlayer[];
}

export const LeagueRanking: React.FC<LeagueRankingProps> = ({ players }) => {
  const [sortBy, setSortBy] = useState<'leaguePoints' | 'rating' | 'winRate' | 'matches'>('leaguePoints');

  // Ordenar jugadores según el criterio seleccionado
  const sortedPlayers = [...players].sort((a, b) => {
    switch (sortBy) {
      case 'leaguePoints':
        return b.league_points - a.league_points;
      case 'rating':
        return b.rating - a.rating;
      case 'winRate':
        const winRateA = a.matches_played > 0 ? (a.matches_won / a.matches_played) * 100 : 0;
        const winRateB = b.matches_played > 0 ? (b.matches_won / b.matches_played) * 100 : 0;
        return winRateB - winRateA;
      case 'matches':
        return b.matches_played - a.matches_played;
      default:
        return b.league_points - a.league_points;
    }
  });

  const getRankIcon = (position: number) => {
    switch (position) {
      case 1:
        return <BsTrophy className="h-5 w-5 text-yellow-500" />;
      case 2:
        return <span className="h-5 w-5 flex items-center justify-center text-lg">🥈</span>;
      case 3:
        return <span className="h-5 w-5 flex items-center justify-center text-lg">🥉</span>;
      default:
        return <span className="h-5 w-5 flex items-center justify-center text-sm font-medium text-gray-500">{position}</span>;
    }
  };

  const getRankBg = (position: number) => {
    switch (position) {
      case 1:
        return 'bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-200';
      case 2:
        return 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200';
      case 3:
        return 'bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200';
      default:
        return 'bg-white border-gray-200';
    }
  };

  const getWinPercentage = (player: LeaguePlayer) => {
    return player.matches_played > 0 ? ((player.matches_won / player.matches_played) * 100).toFixed(1) : '0.0';
  };

  const getRatingColor = (rating: number) => {
    if (players.length === 0) return 'text-gray-900';
    
    const averageRating = players.reduce((acc, p) => acc + p.rating, 0) / players.length;
    const maxRating = Math.max(...players.map(p => p.rating));
    const eightyPercentOfMax = maxRating * 0.8;
    
    if (rating >= eightyPercentOfMax) {
      return 'text-green-600 font-semibold';
    } else if (rating >= averageRating) {
      return 'text-yellow-600 font-medium';
    } else {
      return 'text-red-600';
    }
  };

  if (players.length === 0) {
    return (
      <div className="text-center py-12">
        <BsTrophy className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No hay jugadores</h3>
        <p className="mt-1 text-sm text-gray-500">
          Esta liga aún no tiene jugadores registrados.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controles de ordenamiento */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-lg font-medium text-gray-900 mb-2 sm:mb-0">
          {players.length} {players.length === 1 ? 'jugador' : 'jugadores'}
        </h3>
        
        <div className="flex space-x-2">
            <button
            onClick={() => setSortBy('leaguePoints')}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              sortBy === 'leaguePoints'
                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Puntos de Liga
          </button>
          <button
            onClick={() => setSortBy('rating')}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              sortBy === 'rating'
                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Rating
          </button>
          <button
            onClick={() => setSortBy('winRate')}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              sortBy === 'winRate'
                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            % Victorias
          </button>
          <button
            onClick={() => setSortBy('matches')}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              sortBy === 'matches'
                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Partidas
          </button>
        </div>
      </div>

      {/* Tabla de ranking */}
      <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
        <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                #
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Jugador
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Puntos de liga
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rating
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Partidas
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                V - E - D
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                % Victoria
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedPlayers.map((player, index) => (
              <tr
                key={player.player}
                className={`transition-all duration-200 hover:shadow-md ${getRankBg(index + 1)}`}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {getRankIcon(index + 1)}
                    <span className="ml-2 text-sm font-medium text-gray-900">
                      #{index + 1}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {player.name}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {player.league_points}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`text-lg font-bold ${getRatingColor(player.rating)}`}>
                    {Math.round(player.rating)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {player.matches_played}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2 text-sm">
                    <span className="text-green-600 font-semibold">{player.matches_won}</span>
                    <span className="text-gray-400">-</span>
                    <span className="text-yellow-600 font-semibold">{player.matches_drawn || 0}</span>
                    <span className="text-gray-400">-</span>
                    <span className="text-red-600 font-semibold">{player.matches_lost || 0}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${getWinPercentage(player)}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {getWinPercentage(player)}%
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Estadísticas adicionales */}
      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">
            {players.length}
          </div>
          <div className="text-sm text-gray-500">Total Jugadores</div>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">
            {players.length > 0 ? Math.round(players.reduce((acc, player) => acc + player.rating, 0) / players.length) : 0}
          </div>
          <div className="text-sm text-gray-500">Rating Promedio</div>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">
            {players.length > 0 ? Math.max(...players.map(p => p.rating)) : 0}
          </div>
          <div className="text-sm text-gray-500">Rating Máximo</div>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">
            {players.reduce((acc, player) => acc + player.matches_played, 0)}
          </div>
          <div className="text-sm text-gray-500">Total Partidas</div>
        </div>
      </div>
    </div>
  );
};
